import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useState } from 'react';

export type Toggle = (sectionName: string) => void;
export type IsExpanded = (sectionName: string) => boolean;
type UseAccordionResult = [Toggle, IsExpanded];
type Sections = string[];

interface UseToggleOptions {
  isCollapsible: boolean;
  expandedSections: string[];
  setExpandedSections: Dispatch<SetStateAction<Sections>>;
}

const filterOutCollapsed = (expandedSections: Sections, sectionName: string) =>
  expandedSections.filter(name => name !== sectionName);

const getIsSectionExpanded = (expandedSections: Sections, sectionName: string) =>
  expandedSections.includes(sectionName);

const getSections = (expandedSections: Sections, sectionName: string) =>
  expandedSections.includes(sectionName)
    ? filterOutCollapsed(expandedSections, sectionName)
    : [...expandedSections, sectionName];

const useToggleHandler =
  ({ isCollapsible, expandedSections, setExpandedSections }: UseToggleOptions) =>
  (sectionName: string) => {
    if (!isCollapsible) {
      return;
    }

    const newSections = getSections(expandedSections, sectionName);

    setExpandedSections(newSections);
  };

export const useAccordion = (
  isCollapsible = true,
  defaultExpandedSections: Sections = []
): UseAccordionResult => {
  const [expandedSections, setExpandedSections] = useState<Sections>(defaultExpandedSections);
  const toggleHandler = useToggleHandler({
    isCollapsible,
    expandedSections,
    setExpandedSections,
  });
  const isSectionExpanded = (name: string) =>
    !isCollapsible || getIsSectionExpanded(expandedSections, name);

  return [
    useCallback(toggleHandler, [isCollapsible, expandedSections]),
    useCallback(isSectionExpanded, [isCollapsible, expandedSections]),
  ];
};

export const useAccordionToggle = (identifier: string) => {
  const [toggle, isExpanded] = useAccordion();
  const toggleAccordion = useCallback(() => toggle(identifier), [toggle, identifier]);
  const expanded = isExpanded(identifier);

  return [expanded, toggleAccordion] as const;
};
